import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Users, User, Baby, Calendar, Mail, Shield, LogOut, Settings, Sprout, Key, Tag, Trash2 } from 'lucide-react';
import { Button, Card } from '../components/ui';
import AdminDepositsPanel from '../components/AdminDepositsPanel';
import AdminSignupTokensPanel from '../components/AdminSignupTokensPanel';

const AdminDashboard = ({ currentUser, onLogout }) => {
  const [families, setFamilies] = useState([]);
  const [expandedFamilies, setExpandedFamilies] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('families'); // 'families', 'deposits', 'tokens'
  const [selectedCode, setSelectedCode] = useState('all'); // Filter by signup code
  const [deletingFamily, setDeletingFamily] = useState(null);
  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';


  // Load all families with their members
  useEffect(() => {
    const loadFamilies = async () => {
      try {
        setLoading(true);
        console.log('AdminDashboard: Loading families from API');
        
        const response = await fetch(`${API_BASE_URL}/admin/families`);
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || `Server error: ${response.status}`);
        }
        
        if (result.success) {
          console.log('AdminDashboard: Loaded families:', result.data.length);
          setFamilies(result.data || []);
        } else {
          throw new Error(result.error || 'Failed to load families');
        }
        
      } catch (error) {
        console.error('AdminDashboard: Error loading families:', error);
        setError('Failed to load families: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadFamilies();
  }, []);

  const handleDeleteFamily = async (e, familyId, familyName) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the "${familyName}" family? This will permanently delete all children, guardians, and their data within this family. This action cannot be undone.`)) {
      return;
    }
    try {
      setDeletingFamily(familyId);
      const response = await fetch(`${API_BASE_URL}/admin/families/${familyId}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }
      setFamilies(prev => prev.filter(f => f.id !== familyId));
      setExpandedFamilies(prev => {
        const next = new Set(prev);
        next.delete(familyId);
        return next;
      });
    } catch (error) {
      console.error('AdminDashboard: Error deleting family:', error);
      alert('Failed to delete family: ' + error.message);
    } finally {
      setDeletingFamily(null);
    }
  };

  const toggleFamily = (familyId) => {
    const newExpanded = new Set(expandedFamilies);
    if (newExpanded.has(familyId)) {
      newExpanded.delete(familyId);
    } else {
      newExpanded.add(familyId);
    }
    setExpandedFamilies(newExpanded);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatLastLogin = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const getAvatarEmoji = (avatar) => {
    const avatars = {
      sprout: '🌱',
      leaf: '🌿',
      tree: '🌳'
    };
    return avatars[avatar] || '🌱';
  };

  const getTotalFamilySavings = (children) => {
    return children.reduce((total, child) => total + (child.total_saved || 0), 0);
  };

  // Get unique signup codes for filter dropdown
  const signupCodes = useMemo(() => {
    const codes = new Set();
    families.forEach(f => {
      if (f.signup_code) codes.add(f.signup_code);
    });
    return Array.from(codes).sort();
  }, [families]);

  // Filter families by selected code
  const filteredFamilies = useMemo(() => {
    if (selectedCode === 'all') return families;
    if (selectedCode === 'none') return families.filter(f => !f.signup_code);
    return families.filter(f => f.signup_code === selectedCode);
  }, [families, selectedCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading families...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50">
      {/* Header */}
      <div className="border-b border-gray-200/80 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
                <p className="text-gray-600 text-sm">GrowthSprouts Administration</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-700 font-medium">{currentUser?.email}</span>
              <Button variant="ghost" size="sm" className="rounded-xl">
                <Settings className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={onLogout} className="rounded-xl">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-2xl w-fit">
          <button
            onClick={() => setActiveTab('families')}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'families'
                ? 'bg-white text-gray-900 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>All Families</span>
            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-semibold">
              {families.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'programs'
                ? 'bg-white text-gray-900 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Tag className="w-4 h-4" />
            <span>By Program</span>
            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-semibold">
              {signupCodes.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('deposits')}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'deposits'
                ? 'bg-white text-gray-900 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Sprout className="w-4 h-4" />
            <span>Deposits</span>
          </button>
          <button
            onClick={() => setActiveTab('tokens')}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'tokens'
                ? 'bg-white text-gray-900 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Key className="w-4 h-4" />
            <span>Signup Tokens</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'families' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">All Families</h2>
                <p className="text-gray-600">Monitor and manage family accounts</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-4 py-2 rounded-xl font-medium shadow-lg">
                {families.length} families
              </div>
            </div>

            <div className="space-y-4">
              {families.map((family) => {
                const isExpanded = expandedFamilies.has(family.id);
                const totalSavings = getTotalFamilySavings(family.children);

                return (
                  <Card key={family.id} className="overflow-hidden shadow-lg">
                    {/* Family Header */}
                    <div
                      className="p-6 cursor-pointer transition-all duration-200 hover:bg-gray-50/80"
                      onClick={() => toggleFamily(family.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="transition-transform duration-200">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <img src="/logo.svg" alt="GrowthSprouts" className="w-12 h-12" />
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="text-xl font-bold text-gray-900">{family.name}</h3>
                              {family.program && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {family.program}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm">
                              Created {formatDate(family.created_at)}
                              {family.signup_code && (
                                <span className="ml-2 text-purple-600 font-medium">
                                  Code: {family.signup_code}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-6 text-sm">
                            <div className="text-center">
                              <div className="flex items-center text-blue-600 font-semibold">
                                <Users className="w-4 h-4 mr-1" />
                                {family.guardians.length}
                              </div>
                              <div className="text-xs text-gray-500">guardians</div>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center text-green-600 font-semibold">
                                <Baby className="w-4 h-4 mr-1" />
                                {family.children.length}
                              </div>
                              <div className="text-xs text-gray-500">children</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                                ${totalSavings.toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-500">total saved</div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => handleDeleteFamily(e, family.id, family.name)}
                            disabled={deletingFamily === family.id}
                            className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200 disabled:opacity-50"
                            title="Delete family"
                          >
                            {deletingFamily === family.id ? (
                              <div className="w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Family Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200/50 bg-gradient-to-br from-gray-50/50 to-white/50 backdrop-blur-sm">
                        <div className="p-6 space-y-6">
                          {/* Guardians */}
                          <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3">
                                <Users className="w-4 h-4 text-white" />
                              </div>
                              Guardians ({family.guardians.length})
                            </h4>
                            <div className="grid grid-cols-1 gap-3">
                              {family.guardians.map((guardian) => (
                                <div key={guardian.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-blue-200/50 shadow-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                                        <User className="w-5 h-5 text-white" />
                                      </div>
                                      <div>
                                        <p className="font-bold text-gray-900">
                                          {guardian.first_name} {guardian.last_name}
                                        </p>
                                        <p className="text-sm text-gray-600 flex items-center">
                                          <Mail className="w-3 h-3 mr-1" />
                                          {guardian.email}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-1">
                                        {guardian.role.toUpperCase()}
                                      </div>
                                      <div className="text-xs text-gray-500 flex items-center">
                                        <Calendar className="w-3 h-3 mr-1" />
                                        {formatLastLogin(guardian.last_login)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Children */}
                          <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                                <Baby className="w-4 h-4 text-white" />
                              </div>
                              Children ({family.children.length})
                            </h4>
                            <div className="grid grid-cols-1 gap-3">
                              {family.children.map((child) => (
                                <div key={child.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-green-200/50 shadow-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md text-lg">
                                        {getAvatarEmoji(child.avatar)}
                                      </div>
                                      <div>
                                        <p className="font-bold text-gray-900">
                                          {child.first_name} {child.last_name}
                                        </p>
                                        <p className="text-sm text-gray-600">Age {child.age}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                                        ${child.total_saved.toFixed(2)}
                                      </div>
                                      <div className="text-xs text-gray-500 flex items-center">
                                        <Calendar className="w-3 h-3 mr-1" />
                                        {formatDate(child.created_at)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* By Program Tab */}
        {activeTab === 'programs' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Families by Program</h2>
                <p className="text-gray-600">Filter and view families by signup code</p>
              </div>
            </div>

            {/* Program Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCode('all')}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                  selectedCode === 'all'
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-emerald-300'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>All</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  selectedCode === 'all' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {families.length}
                </span>
              </button>
              <button
                onClick={() => setSelectedCode('none')}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                  selectedCode === 'none'
                    ? 'bg-gray-700 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'
                }`}
              >
                <span>No Code</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  selectedCode === 'none' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {families.filter(f => !f.signup_code).length}
                </span>
              </button>
              {signupCodes.map(code => (
                <button
                  key={code}
                  onClick={() => setSelectedCode(code)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                    selectedCode === code
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <Tag className="w-4 h-4" />
                  <span>{code}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    selectedCode === code ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {families.filter(f => f.signup_code === code).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Results Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700">
                {selectedCode === 'all' ? 'All Families' :
                 selectedCode === 'none' ? 'Families without a code' :
                 `${selectedCode} Families`}
              </h3>
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-2 rounded-xl font-medium shadow-lg">
                {filteredFamilies.length} families
              </div>
            </div>

            {/* Filtered Family List */}
            <div className="space-y-4">
              {filteredFamilies.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-gray-500">No families found for this filter</p>
                </Card>
              ) : (
                filteredFamilies.map((family) => {
                  const isExpanded = expandedFamilies.has(family.id);
                  const totalSavings = getTotalFamilySavings(family.children);

                  return (
                    <Card key={family.id} className="overflow-hidden shadow-lg">
                      <div
                        className="p-6 cursor-pointer transition-all duration-200 hover:bg-gray-50/80"
                        onClick={() => toggleFamily(family.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="transition-transform duration-200">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-500" />
                              )}
                            </div>
                            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center p-2">
                              <img src="/logo.svg" alt="GrowthSprouts" className="w-full h-full" />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h3 className="text-xl font-bold text-gray-900">{family.name}</h3>
                                {family.program && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    <Tag className="w-3 h-3 mr-1" />
                                    {family.program}
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 text-sm">
                                Created {formatDate(family.created_at)}
                                {family.signup_code && (
                                  <span className="ml-2 text-purple-600 font-medium">
                                    Code: {family.signup_code}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-6 text-sm">
                              <div className="text-center">
                                <div className="flex items-center text-blue-600 font-semibold">
                                  <Users className="w-4 h-4 mr-1" />
                                  {family.guardians.length}
                                </div>
                                <div className="text-xs text-gray-500">guardians</div>
                              </div>
                              <div className="text-center">
                                <div className="flex items-center text-green-600 font-semibold">
                                  <Baby className="w-4 h-4 mr-1" />
                                  {family.children.length}
                                </div>
                                <div className="text-xs text-gray-500">children</div>
                              </div>
                              <div className="text-center">
                                <div className="text-lg font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                                  ${totalSavings.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-500">total saved</div>
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleDeleteFamily(e, family.id, family.name)}
                              disabled={deletingFamily === family.id}
                              className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200 disabled:opacity-50"
                              title="Delete family"
                            >
                              {deletingFamily === family.id ? (
                                <div className="w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Trash2 className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-200/50 bg-gradient-to-br from-gray-50/50 to-white/50 backdrop-blur-sm">
                          <div className="p-6 space-y-6">
                            {/* Guardians */}
                            <div>
                              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3">
                                  <Users className="w-4 h-4 text-white" />
                                </div>
                                Guardians ({family.guardians.length})
                              </h4>
                              <div className="grid grid-cols-1 gap-3">
                                {family.guardians.map((guardian) => (
                                  <div key={guardian.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-blue-200/50 shadow-lg">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                                          <User className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                          <p className="font-bold text-gray-900">
                                            {guardian.first_name} {guardian.last_name}
                                          </p>
                                          <p className="text-sm text-gray-600 flex items-center">
                                            <Mail className="w-3 h-3 mr-1" />
                                            {guardian.email}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-1">
                                          {guardian.role.toUpperCase()}
                                        </div>
                                        <div className="text-xs text-gray-500 flex items-center">
                                          <Calendar className="w-3 h-3 mr-1" />
                                          {formatLastLogin(guardian.last_login)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Children */}
                            <div>
                              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                                  <Baby className="w-4 h-4 text-white" />
                                </div>
                                Children ({family.children.length})
                              </h4>
                              <div className="grid grid-cols-1 gap-3">
                                {family.children.map((child) => (
                                  <div key={child.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-green-200/50 shadow-lg">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md text-lg">
                                          {getAvatarEmoji(child.avatar)}
                                        </div>
                                        <div>
                                          <p className="font-bold text-gray-900">
                                            {child.first_name} {child.last_name}
                                          </p>
                                          <p className="text-sm text-gray-600">Age {child.age}</p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                                          ${child.total_saved.toFixed(2)}
                                        </div>
                                        <div className="text-xs text-gray-500 flex items-center">
                                          <Calendar className="w-3 h-3 mr-1" />
                                          {formatDate(child.created_at)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Deposits Tab */}
        {activeTab === 'deposits' && (
          <AdminDepositsPanel currentUser={currentUser} />
        )}

        {/* Signup Tokens Tab */}
        {activeTab === 'tokens' && (
          <AdminSignupTokensPanel currentUser={currentUser} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;